import type { TownEvent } from './types';
import { addDays } from './events';

export const LAGOA_AGENDA_SOURCE = 'Município de Lagoa';
export const LAGOA_AGENDA_URL = 'https://www.cm-lagoa.pt/';

const MONTHS: Record<string, number> = {
  jan: 1,
  janeiro: 1,
  fev: 2,
  fevereiro: 2,
  mar: 3,
  marco: 3,
  março: 3,
  abr: 4,
  abril: 4,
  mai: 5,
  maio: 5,
  jun: 6,
  junho: 6,
  jul: 7,
  julho: 7,
  ago: 8,
  agosto: 8,
  set: 9,
  setembro: 9,
  out: 10,
  outubro: 10,
  nov: 11,
  novembro: 11,
  dez: 12,
  dezembro: 12,
};

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function iso(year: number, month: number, day: number): string | null {
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const dt = new Date(Date.UTC(year, month - 1, day));
  if (dt.getUTCFullYear() !== year || dt.getUTCMonth() !== month - 1 || dt.getUTCDate() !== day) return null;
  return `${year}-${pad(month)}-${pad(day)}`;
}

function yearFrom(token: string, fallback: number): number {
  const n = Number(token);
  if (!Number.isFinite(n)) return fallback;
  return n < 100 ? 2000 + n : n;
}

function textOf(html: string): string {
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#(\d+);/g, (_, n: string) => String.fromCharCode(Number(n)))
    .replace(/\s+/g, ' ')
    .trim();
}

function field(block: string, className: string): string {
  const startRe = new RegExp(`class=["']${className}\\b[^"']*["']`, 'i');
  const start = startRe.exec(block);
  if (!start) return '';
  const from = block.slice(start.index);
  const value = /class=["']widget_value["'][^>]*>/i.exec(from);
  if (!value) return '';
  const innerStart = value.index + value[0].length;
  let depth = 1;
  const tag = /<\/?div\b[^>]*>/gi;
  tag.lastIndex = innerStart;
  let match: RegExpExecArray | null;
  while ((match = tag.exec(from))) {
    depth += match[0].startsWith('</') ? -1 : 1;
    if (depth === 0) return textOf(from.slice(innerStart, match.index));
  }
  return '';
}

function titleOf(block: string): string {
  const h3 = /<h3[^>]*>([\s\S]*?)<\/h3>/i.exec(block);
  return h3 ? textOf(h3[1]) : '';
}

function hrefOf(block: string): string {
  const href = /href=["']([^"']+)["']/i.exec(block);
  return href?.[1] ?? '';
}

interface ParsedRange {
  start: string;
  end: string;
}

/**
 * Yearless cards ("3 Jan") published in December belong to January of the
 * next year. Dates that already include a year are left alone.
 */
function rollYearlessForward(
  month: number,
  start: string,
  end: string,
  today: string,
  year: number,
): ParsedRange {
  const todayMonth = Number(today.slice(5, 7));
  if (end >= today || todayMonth < 11 || month > 2) return { start, end };
  const nextStart = iso(year + 1, month, Number(start.slice(8, 10)));
  const nextEnd = iso(year + 1, month, Number(end.slice(8, 10)));
  if (!nextStart || !nextEnd) return { start, end };
  return { start: nextStart, end: nextEnd };
}

function parseRange(raw: string, today: string): ParsedRange | null {
  const fallbackYear = Number(today.slice(0, 4));
  const text = raw
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s*\.\s*/g, '.')
    .replace(/\s+/g, ' ')
    .trim();

  const numericRange =
    /(\d{1,2})\.(\d{1,2})\.(\d{2,4})\s*(?:a|-)\s*(\d{1,2})\.(\d{1,2})\.(\d{2,4})/.exec(text);
  if (numericRange) {
    const start = iso(yearFrom(numericRange[3], fallbackYear), Number(numericRange[2]), Number(numericRange[1]));
    const end = iso(yearFrom(numericRange[6], fallbackYear), Number(numericRange[5]), Number(numericRange[4]));
    if (start && end) return start <= end ? { start, end } : { start: end, end: start };
  }

  const namedRange = /(\d{1,2})\s*(?:a|-)\s*(\d{1,2})\s+([a-z]+)/.exec(text);
  if (namedRange && MONTHS[namedRange[3]]) {
    const month = MONTHS[namedRange[3]];
    const year = fallbackYear;
    const start = iso(year, month, Number(namedRange[1]));
    let end = iso(year, month, Number(namedRange[2]));
    if (start && end && end < start) {
      end = iso(year + 1, month, Number(namedRange[2]));
    }
    if (start && end) return rollYearlessForward(month, start, end, today, year);
  }

  const numericDay = /(\d{1,2})\.(\d{1,2})\.(\d{2,4})/.exec(text);
  if (numericDay) {
    const day = iso(yearFrom(numericDay[3], fallbackYear), Number(numericDay[2]), Number(numericDay[1]));
    if (day) return { start: day, end: day };
  }

  const namedDay = /(\d{1,2})\s+([a-z]+)/.exec(text);
  if (namedDay && MONTHS[namedDay[2]]) {
    const month = MONTHS[namedDay[2]];
    const day = iso(fallbackYear, month, Number(namedDay[1]));
    if (day) return rollYearlessForward(month, day, day, today, fallbackYear);
  }

  return null;
}

function parseTimes(schedule: string): { startTime?: string; endTime?: string } {
  const hits = [...schedule.matchAll(/(\d{1,2})\s*(?:h|:)\s*(\d{2})/gi)];
  if (hits.length === 0) return {};
  const toHm = (h: string, m: string) => `${pad(Number(h))}:${m}`;
  const startTime = toHm(hits[0][1], hits[0][2]);
  const endTime = hits[1] ? toHm(hits[1][1], hits[1][2]) : undefined;
  return { startTime, endTime };
}

function blocks(html: string, marker: string): string[] {
  const start = html.indexOf(marker);
  if (start < 0) return [];
  const slice = html.slice(start);
  const end = slice.indexOf('agendaLista_AREA', marker === 'agendaSlider_AREA' ? 10 : 0);
  const region = marker === 'agendaSlider_AREA' && end > 0 ? slice.slice(0, end) : slice;
  const parts = region.split(/<li\b/i).slice(1);
  return parts.map((part) => `<li${part.split(/<\/li>/i)[0]}</li>`);
}

function slugFrom(href: string, title: string): string {
  const tail = href.split('/').filter(Boolean).pop() ?? title;
  const slug = tail
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return slug || 'event';
}

function absoluteUrl(href: string): string | undefined {
  if (!href) return undefined;
  if (href.startsWith('http')) return href;
  return `https://www.cm-lagoa.pt${href.startsWith('/') ? href : `/${href}`}`;
}

/**
 * Reads the public homepage agenda of Câmara Municipal de Lagoa.
 * Returns nothing when the markup has no dated events — callers show an
 * empty state instead of inventing listings.
 */
export function parseLagoaAgenda(html: string, today: string): TownEvent[] {
  const until = addDays(today, 4);
  const found = new Map<string, TownEvent>();

  const regions = [...blocks(html, 'agendaSlider_AREA'), ...blocks(html, 'agendaLista_AREA')];
  for (const block of regions) {
    const title = titleOf(block);
    const href = hrefOf(block);
    const dates = field(block, 'dates');
    const range = parseRange(dates, today);
    if (!title || !range) continue;
    if (range.end < today || range.start > until) continue;

    const id = slugFrom(href, title);
    const location = field(block, 'location').replace(/^Local:\s*/i, '').trim();
    const schedule = field(block, 'schedule').replace(/^Hor[aá]rio:\s*/i, '').trim();
    const times = parseTimes(schedule);
    const showOn = range.start < today ? today : range.start;
    const spanNote = range.start === range.end ? undefined : `${range.start} – ${range.end}`;
    const existing = found.get(id);
    const event: TownEvent = {
      id,
      title,
      date: showOn,
      startTime: times.startTime ?? existing?.startTime,
      endTime: times.endTime ?? existing?.endTime,
      location: location || existing?.location || 'Lagoa',
      notes: spanNote,
      source: LAGOA_AGENDA_SOURCE,
      url: absoluteUrl(href) ?? existing?.url,
    };
    found.set(id, event);
  }

  return [...found.values()].sort(
    (a, b) => a.date.localeCompare(b.date) || a.title.localeCompare(b.title),
  );
}
